import type { ObjectMetadata } from '@olytic/foundation';

import { humanize } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asDate, asDateTime, asLabel, createdByField, documentColumns, field, lastModifiedByField, linkedRecordColumns, lookup, related, type Row } from './kit';

const def = OBJECT_DEFS.activities;

/** An activity: one touch (email, call, message, meeting, note). Rationale: activities.md. */
export const activities: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Activity',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('subject', 'Subject'),
    field('channel', 'Channel', { format: asLabel }),
    field('direction', 'Direction', { format: asLabel }),
    field('occurred_at', 'When', { format: asDateTime }),
    field('owner_name', 'Owner', { link: lookup('people', 'owner_id') }),
    field('participants', 'Participants'),
    field('summary', 'Summary', { wide: true }),
    field('body', 'Body', { wide: true }),
    // A follow-up (docs/gtm-follow-ups.md §3): something to do, with a status and a due date.
    field('kind', 'Type', { format: asLabel }),
    field('status', 'Status', { format: asLabel }),
    field('due_on', 'Due', { format: asDate }),
    field('thread_id', 'Thread ID'),
    field('source', 'Source'),
    field('source_id', 'Source ID'),
    field('created_at', 'Created', { format: asDateTime }),
    field('updated_at', 'Last Modified', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['subject', 'channel', 'direction', 'occurred_at', 'owner_name', 'participants'] },
    { key: 'content', label: 'Content', fields: ['summary', 'body'] },
    { key: 'follow_up', label: 'Follow-Up', fields: ['kind', 'status', 'due_on'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['source', 'source_id', 'thread_id', 'created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    related('links', 'Linked to', undefined, linkedRecordColumns()),
    related('documents', 'Documents', 'documents', [documentColumns()[0]!, documentColumns()[2]!]),
  ],
  titleOf: (row) => (typeof row.subject === 'string' && row.subject !== '' ? row.subject : humanize(row.channel) || 'Activity'),
  searchKeys: ['subject', 'summary'],
};
